export type GateIdentityDecision = "anonymous" | "authenticated" | "fail-open";

/**
 * Probe the same-origin auth-optional session endpoint before hydrating a
 * server-rendered anonymous gate. Only a valid 200 response makes a session
 * decision; timeouts, network failures, malformed bodies, and unexpected
 * statuses fail open to the full application so staff cannot be locked out.
 */
export async function probeGateIdentity(
  fetcher: typeof fetch = fetch,
  timeoutMs = 2_000,
): Promise<GateIdentityDecision> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetcher("/api/platform/session", {
      cache: "no-store",
      credentials: "same-origin",
      headers: { accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) return "fail-open";
    const payload: unknown = await response.json();
    if (
      typeof payload !== "object" ||
      payload === null ||
      typeof (payload as { authenticated?: unknown }).authenticated !== "boolean"
    ) {
      return "fail-open";
    }
    return (payload as { authenticated: boolean }).authenticated
      ? "authenticated"
      : "anonymous";
  } catch {
    return "fail-open";
  } finally {
    globalThis.clearTimeout(timeout);
  }
}